import {
	PrivateInvestorDocumentRedirectParams,
	PrivateInvestorDocumentType,
	PrivateUserDocumentRedirectParams,
	PrivateUserDocumentType
} from 'types/investor-documents';
import { privateInvestorDocumentViewRedirect, privateUserDocumentViewRedirect } from '@api/private-document-redirect';
import { FundriseRouteConfig } from 'vue-router';
import { openNewTab } from '@utils/client';
import { triggerDownload } from '@utils/request';
import { useAppStore } from '@stores/app';

const openPrivateDocument = async (redirectUrl: string) => {
	const appStore = useAppStore();

	if (appStore.isMobileWebview) {
		triggerDownload(redirectUrl);
	} else if (appStore.isCorniceSubstituteUser) {
		openNewTab(redirectUrl);
	} else {
		window.location.href = redirectUrl;
	}
};

const privateDocumentRedirectRoutes: Array<FundriseRouteConfig> = [
	{
		path: '/shareholder-documents/:id/view',
		name: 'shareholder-document-view',
		meta: {
			minTokenScopeRequired: 'FULL_ACCESS',
			additionalAllowedScopes: ['ADVISOR_ACCESS', 'ADVISOR_PRIVILEGED_ACCESS', 'ADVISOR_READ_ACCESS']
		},
		beforeEnter: async (to) => {
			const params: PrivateInvestorDocumentRedirectParams = {
				documentId: to.params.id as string,
				documentType: 'SHAREHOLDER'
			};

			try {
				const redirectLink = await privateInvestorDocumentViewRedirect(params);
				await openPrivateDocument(redirectLink.presignedUrl);
				return false;
			} catch {
				return { name: 'account-overview', replace: true };
			}
		},
		// revisit using redirect
		component: () => import('@views/account/account-overview.vue')
	},
	{
		path: '/investor-verification-documents/:id/view',
		name: 'investor-verification-document-view',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to) => {
			const params: PrivateInvestorDocumentRedirectParams = {
				documentId: to.params.id as string,
				documentType: 'INVESTOR_VERIFICATION'
			};

			try {
				const redirectLink = await privateInvestorDocumentViewRedirect(params);
				await openPrivateDocument(redirectLink.presignedUrl);
				return false;
			} catch {
				return { name: 'account-overview', replace: true };
			}
		},
		// revisit using redirect
		component: () => import('@views/account/account-overview.vue')
	},
	{
		path: '/identification-documents/:id/view',
		name: 'identification-document-view',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to) => {
			const params: PrivateInvestorDocumentRedirectParams = {
				documentId: to.params.id as string,
				documentType: 'IDENTIFICATION'
			};

			try {
				const redirectLink = await privateInvestorDocumentViewRedirect(params);
				await openPrivateDocument(redirectLink.presignedUrl);
				return false;
			} catch {
				return { name: 'account-overview', replace: true };
			}
		},
		// revisit using redirect
		component: () => import('@views/account/account-overview.vue')
	},
	{
		path: '/private-investor-documents/:documentType/:documentId/view',
		name: 'private-investor-documents-redirect',
		meta: {
			minTokenScopeRequired: 'FULL_ACCESS',
			additionalAllowedScopes: ['ADVISOR_ACCESS', 'ADVISOR_PRIVILEGED_ACCESS', 'ADVISOR_READ_ACCESS']
		},
		beforeEnter: async (to) => {
			const params: PrivateInvestorDocumentRedirectParams = {
				documentId: to.params.documentId as string,
				documentType: to.params.documentType as PrivateInvestorDocumentType
			};

			try {
				const redirectLink = await privateInvestorDocumentViewRedirect(params);
				await openPrivateDocument(redirectLink.presignedUrl);
				return false;
			} catch {
				return { name: 'account-overview', replace: true };
			}
		},
		// revisit using redirect
		component: () => import('@views/account/account-overview.vue')
	},
	{
		path: '/private-user-documents/:documentType/:documentId/view',
		name: 'private-user-documents-redirect',
		meta: {
			minTokenScopeRequired: 'FULL_ACCESS',
			additionalAllowedScopes: ['ADVISOR_ACCESS', 'ADVISOR_PRIVILEGED_ACCESS', 'ADVISOR_READ_ACCESS']
		},
		beforeEnter: async (to) => {
			const params: PrivateUserDocumentRedirectParams = {
				documentId: to.params.documentId as string,
				documentType: to.params.documentType as PrivateUserDocumentType
			};

			try {
				const redirectLink = await privateUserDocumentViewRedirect(params);
				await openPrivateDocument(redirectLink.presignedUrl);
				return false;
			} catch {
				return { name: 'account-overview', replace: true };
			}
		},
		// revisit using redirect
		component: () => import('@views/account/account-overview.vue')
	}
];

export default privateDocumentRedirectRoutes;
